import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  Switch,
  Tab,
  Tabs,
} from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import { useState } from "react";
import CustomTabPanel from "src/components/CustomTabPanel";
import TabelaDinamicaPermissoes from "src/components/TabelaDinamicaPermissao";

const RotaFormSchema = z
  .object({
    idRota: z.number().readonly(),
    caminho: z.string().min(1, {
      message: "Caminho da API é obrigatório",
    }),
    descricao: z.string().min(1, {
      message: "Descrição da API é obrigatório",
    }),
    ativo: z.boolean(),
    logAtivo: z.boolean(),
    vinculos: z.any(),
  })
  .partial({
    idRota: true,
    vinculos: true,
  })
  .required({
    caminho: true,
    descricao: true,
    ativo: true,
    logAtivo: true,
  });

type RotaFormData = z.infer<typeof RotaFormSchema>;

type RotaFormProps = {
  rotaValues: RotaFormData;
  rotaPermissionData: any;
  loading: boolean;
  onSubmit(data: RotaFormData): void;
  onClose(event: any): void;
};

export default function RotaForm({
  rotaValues,
  rotaPermissionData,
  loading,
  onSubmit,
  onClose,
}: RotaFormProps) {
  const [activeTab, setActiveTab] = useState(0);

  const columnsPermissoes = [
    { id: "menu", label: "Menu", field: null },
    { id: "pode_inserir", label: "Pode inserir", field: "PODE_INSERIR" },
    { id: "atualizar", label: "Atualizar", field: "PODE_EDITAR" },
    { id: "selecionar", label: "Selecionar", field: "PODE_SELECIONAR" },
    { id: "excluir", label: "Excluir", field: "PODE_EXCLUIR" },
    { id: "todos", label: "Todos", field: null },
  ];

  function setupTab(index: number) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const handleTabChange = (event: any, newValue: any) => {
    setActiveTab(newValue);
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<RotaFormData>({
    mode: "onChange",
    resolver: zodResolver(RotaFormSchema),
    defaultValues: {
      idRota: rotaValues.idRota || undefined,
      caminho: rotaValues.caminho || "",
      descricao: rotaValues.descricao || "",
      ativo: rotaValues.ativo || false,
      logAtivo: rotaValues.logAtivo || false,
      vinculos: rotaValues.vinculos || [],
    },
  });

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  const submit = (data: RotaFormData) => {
    var vinculosAjustados: any = [];

    data.vinculos.forEach((vinculo: any) => {
      if (
        !(
          vinculo.funcionalidades.length === 0 ||
          (vinculo.funcionalidades.length === 1 &&
            vinculo.funcionalidades[0] === null)
        )
      ) {
        vinculosAjustados.push(vinculo);
      }
    });

    const body: any = {
      dadosRota: {
        caminho: data.caminho,
        descricao: data.descricao,
        ativo: data.ativo,
        logAtivo: data.logAtivo,
        vinculos: vinculosAjustados,
      },
    };

    onSubmit(body);
  };

  const handlePermsChange = (newSubmenuPerms: any) => {
    const subMenuPermsIndex = rotaValues.vinculos.findIndex(
      (perm: any) => perm.idMenu === newSubmenuPerms.idMenu
    );

    if (
      subMenuPermsIndex !== -1 &&
      newSubmenuPerms.funcionalidades.length === 0
    ) {
      rotaValues.vinculos.splice(subMenuPermsIndex, 1);
    } else if (subMenuPermsIndex !== -1) {
      rotaValues.vinculos[subMenuPermsIndex] = { ...newSubmenuPerms };
    } else if (newSubmenuPerms.funcionalidades.length > 0) {
      rotaValues.vinculos.push({ ...newSubmenuPerms });
    }
    setValue("vinculos", rotaValues.vinculos, { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Dados Básicos" {...setupTab(0)} />
              <Tab label="Menus" {...setupTab(1)} />
            </Tabs>
          </Box>
          <CustomTabPanel value={activeTab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <Controller
                  name="idRota"
                  control={control}
                  defaultValue={rotaValues.idRota}
                  render={({ field: { ref, ...field } }) => (
                    <GenericTextField
                      disabled
                      label="ID"
                      error={Boolean(errors.idRota)}
                      helperText={errors.idRota?.message}
                      placeholder={"Nova rota"}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={10}>
                <Controller
                  name="caminho"
                  control={control}
                  defaultValue={rotaValues.caminho}
                  render={({ field: { ref, ...field } }) => (
                    <GenericTextField
                      required
                      label="Caminho"
                      placeholder="Digite o caminho da API"
                      error={Boolean(errors.caminho)}
                      helperText={errors.caminho?.message}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="descricao"
                  control={control}
                  defaultValue={rotaValues.descricao}
                  render={({ field: { ref, ...field } }) => (
                    <GenericTextField
                      required
                      label="Descrição"
                      placeholder="Digite o descricao da rota de API"
                      error={Boolean(errors.descricao)}
                      helperText={errors.descricao?.message}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <Controller
                  name="ativo"
                  control={control}
                  defaultValue={rotaValues.ativo}
                  render={({ field: { ref, ...field } }) => (
                    <FormControlLabel
                      control={<Switch color="primary" />}
                      label="Ativo"
                      labelPlacement="start"
                      checked={field.value}
                      sx={{
                        margin: 0,
                      }}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <Controller
                  name="logAtivo"
                  control={control}
                  defaultValue={rotaValues.logAtivo}
                  render={({ field: { ref, ...field } }) => (
                    <FormControlLabel
                      control={<Switch color="primary" />}
                      label="Logs"
                      labelPlacement="start"
                      checked={field.value}
                      sx={{
                        margin: 0,
                      }}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CustomTabPanel>
          <CustomTabPanel
            value={activeTab}
            index={1}
            className={styles.tabelaPermissoes}
          >
            <TabelaDinamicaPermissoes
              data={rotaPermissionData}
              columns={columnsPermissoes}
              values={rotaValues.vinculos}
              loading={loading}
              handleChangeCallback={handlePermsChange}
            />
          </CustomTabPanel>
        </Box>
        <Box>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </form>
  );
}
